import type { Vue3TouchEventsOptions } from 'vue3-touch-events';
import * as Sentry from '@sentry/vue';
import urqlPlugin from '@urql/vue';
import { vTooltip } from 'floating-vue';
import PDFObjectPlugin from 'pdfobject-vue';
import { createPinia } from 'pinia';
import { createApp } from 'vue';
import Vue3TouchEvents from 'vue3-touch-events';
import App from './App.vue';
import { registerProviders } from './bootstrap';
import useSentry from './composables/useSentry';
import urqlClient from './graphql/client';
import posthogPlugin from './plugins/posthog';
import { VueQueryPlugin, vueQueryPluginOptions } from './plugins/vueQuery';
import router from './router';
import { narrativeMerchantsPlugin } from './stores/narrative';
import { usePopupNotificationsStore } from './stores/overlays/popupNotifications';
import { useSessionExpiredStore } from './stores/overlays/sessionExpired';
import '../css/app.css';

// Suppress the unhandled vite:preloadError event — chunk load failures during
// navigation are handled in the router's onError hook, which reloads to the
// correct destination URL rather than the current one.
window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
});

// Handle CSS preload failures (deployment/cache mismatches)
window.addEventListener('unhandledrejection', (event) => {
    // Filter out CustomEvent rejections (browser extension noise - PREPBUSINESS-71X)
    // See: https://github.com/getsentry/sentry-javascript/issues/2210
    if (event.reason instanceof CustomEvent) {
        event.preventDefault();
        return;
    }

    // When the session has expired, client.ts already shows the SessionExpired modal
    // and returns a synthetic GraphQL error. The resulting CombinedError propagates as
    // an unhandled rejection but is already handled — suppress it (PREPBUSINESS-ACB).
    try {
        if (
            useSessionExpiredStore().getSessionExpired === true
            && event.reason?.message?.includes('Your session has expired') === true
        ) {
            event.preventDefault();
            return;
        }
    } catch (_) {
        // Pinia not yet initialised — skip session-expiry check
    }

    if (event.reason?.message?.includes('Unable to preload CSS') === true) {
        usePopupNotificationsStore().newPopupNotification({
            type: 'action',
            title: 'A new version is available. Please refresh the page.',
            actionText: 'Refresh',
            action: () => window.location.reload(),
            permanent: true,
        });
        event.preventDefault();
    }

    // Log breadcrumb for non-Error rejections to help debug source
    if (!(event.reason instanceof Error)) {
        Sentry.addBreadcrumb({
            category: 'unhandledrejection',
            message: 'Non-Error rejection',
            level: 'warning',
            data: {
                reason: String(event.reason),
                type: typeof event.reason,
                constructor: event.reason?.constructor?.name,
                isAxiosError: event.reason?.isAxiosError,
            },
        });
    }
});

const app = createApp(App);
const pinia = createPinia();
pinia.use(narrativeMerchantsPlugin);

app.use(pinia);

// Initialise Sentry synchronously before app.mount() so the @sentry/vue integration
// installs Vue's error handler in time to capture errors thrown during the initial
// render and lifecycle, and so browser tracing records the first navigation. Gated to
// production; identity/tenant context is attached reactively once the narrative loads.
const sentryDsn = import.meta.env.VITE_SENTRY_PUBLIC_DSN;
if (import.meta.env.VITE_APP_ENV === 'production' && sentryDsn != null && sentryDsn !== '') {
    useSentry().initSentry(app, sentryDsn, import.meta.env.VITE_RELEASE_TAG ?? '');
}
app.use(VueQueryPlugin, vueQueryPluginOptions);
app.use(router);
app.use(urqlPlugin, urqlClient);
app.use(posthogPlugin);
app.use<Vue3TouchEventsOptions>(Vue3TouchEvents, {
    disableClick: false,
});
app.use(PDFObjectPlugin);
app.directive('tooltip', vTooltip);

registerProviders(app);

app.mount('#app');
